
<template>
  <div
    id="app"
    :class="{'is-safari':isSafari}">
    <Loader
      v-if="loader && isRedirectedFromBranch && !isInternationalUser"
      label=""
      absolute
      center
      with-overlay
    />
    <div class="main-container">
      <Navbar/>
      <transition
        name="fade"
        mode="out-in"
      >
        <router-view
          :key="$route.fullPath"
          @toggleNavBar="toggleNavBar"/>
      </transition>
      <audio-player/>
      <FooterDark
        v-if="!isInternationalUser"
      />
      <InternationalFooter
        v-if="isInternationalUser"
      />
    </div>

    <transition name="slide-up">
      <GetMobileApp
        v-if="eligibleForMobileBanner && !deniedMobileBanner && !tappedDownloadOnMobileBanner"
        @onTapDownload="onTapDownload" />
    </transition>
    <transition name="slide-up">
      <CookiePolicy
        v-if="showCookiePolicy"
      />
    </transition>
    <promo-offer-modal
      :show="showPromoOfferModal"
      :promo-data="promoData"
      @closePromoOfferModal="closePromoOfferModal"
    />
  </div>
</template>
<script>
// import InstaBug from 'instabug-sdk'
import * as log from 'loglevel';
import Navbar from './components/Navbar';
import FooterDark from './components/FooterDark';
import AudioPlayer from './components/AudioPlayer';
import InternationalFooter from './components/InternationalFooter';
import GetMobileApp from './components/GetMobileApp';
import CookiePolicy from './components/CookiePolicy';
import PromoOfferModal from './components/PromoOfferModal';
import Loader from './components/Loader';
import firebase from './config/firebase';
import EpisodeService from './services/Episode';
import PlaybackService from './services/PlaybackService';
import Api from './services/Api';
import User from './services/User';
import Branch from './services/Branch';
import Event from './services/PlayerEvent';
import Subscription from './services/Subscription';
import Countries from './assets/json/countries.json';
import euCountries from './assets/json/eu_countries.json';
import Creators from './assets/json/creators.json';

export default {
  components: {
    GetMobileApp,
    CookiePolicy,
    'audio-player': AudioPlayer,
    Navbar,
    FooterDark,
    InternationalFooter,
    Loader,
    'promo-offer-modal': PromoOfferModal,
  },
  data() {
    return {
      showNavBar: false,
      isLoggedIn: false,
      apiService: new Api(),
      userService: new User(),
      branchService: new Branch(),
      eligibleForMobileBanner: false,
      tappedDownloadOnMobileBanner: false,
      episodeService: new EpisodeService(),
      playbackService: new PlaybackService(),
      eventProvider: new Event(),
      subscriptionProvider: new Subscription(),
      countries: Countries,
      euCountries,
      defaultCurrency: '',
      showPromoOfferModal: false,
      promoData: {},
      loader: false,
      isSafari: !!navigator.userAgent.match(/safari/i) && !navigator.userAgent.match(/chrome/i) && typeof document.body.style.webkitFilter !== 'undefined' && !window.chrome,
      creators: Creators,
      clientX: 0,
      clientY: 0,
      firstClientX: 0,
      firstClientY: 0,
    };
  },
  computed: {
    user() {
      return this.$store.getters.user;
    },
    deniedMobileBanner() {
      return this.$store.getters.denied_mobile_banner;
    },
    libPodcastsIds() {
      return this.$store.getters.libraryPodcasts;
    },
    libEpisodesIds() {
      return this.$store.getters.libraryEpisodes;
    },
    showCookiePolicy() {
      return !this.$store.getters.acceptedCookiePolicy && this.inEU;
    },
    inEU() {
      const { country } = this.$store.getters;

      if (country) {
        return euCountries.find(euCountry => euCountry.code === country.toUpperCase());
      }

      return false;
    },
    isRedirectedFromBranch() {
      const urlParams = new URLSearchParams(window.location.search);
      const hasBranchMatchId = urlParams.get('_branch_match_id');
      return hasBranchMatchId;
    },
  },
  mounted() {
    this.disableVerticalScrolling();
    if (window.isTouch && window.outerWidth <= 1024) {
      this.eligibleForMobileBanner = true;
    }
    const ctx = this;
    window.addEventListener('beforeunload', () => {
      ctx.eventProvider.triggerRefreshEvent();
    });

    this.fetchSubscriptionPlans();
    this.getBrowsersFingerPrintId();

    setTimeout(() => {
      this.$analytics.track('app_loaded');
    }, 2000);
  },
  beforeMount() {
    this.setUserCountry();
    // attached with window to be available in services
    window.$store = this.$store;
    window.$analytics = this.$analytics;
    // let token = this.$store.getters.token
    // this.userService.setUserToken(token)

    const context = this;

    firebase.auth().onAuthStateChanged((user) => {
      if (user) {
        const isLoggedIn = this.$store.getters.loggedIn;
        if (isLoggedIn) {
          this.verifyUserToken();
          this.getUserEpisodeListens();
          this.$store.dispatch('populateUserLibrary');

          if (context.user && context.user.is_subscribed) {
            this.fetchSubscription();
          }
        }
        // const { activePlan } = this.$store.getters;
        // if (!activePlan && user) {
        //   this.fetchSubscriptionPlans();
        // }
      } else {
        this.userService.logout();
        this.$store.dispatch('clearAllEpisodesProgress');
      }
      // unsubscribe()
      // console.log("ENV")
      // console.log(process.env.LUMINARY_GOOGLE_ANALYTICS_ID)
    });
    this.triggerLoadAnalytics();
  },
  methods: {
    disableVerticalScrolling() {
      if (!window || !window.isTouch || !this.isBrowserSafari()) return;
      // Disable vertical scrolling during horizontal swipe. Introduced for safari.
      window.addEventListener('touchstart', this.touchStart);
      window.addEventListener('touchmove', this.preventTouch, { passive: false });
    },
    touchStart(e) {
      this.firstClientX = e.touches[0].clientX;
      this.firstClientY = e.touches[0].clientY;
    },
    /* eslint-disable */
    preventTouch(e) {
      const minValue = 5; // threshold

      this.clientX = e.touches[0].clientX - this.firstClientX;
      this.clientY = e.touches[0].clientY - this.firstClientY;

      // Vertical scrolling does not work when you start swiping horizontally.
      if (Math.abs(this.clientX) > minValue) {
        e.preventDefault();
        e.returnValue = false;
        return false;
      }
    },
    verifyUserToken() {
      this.userService.verifyAuthentication().then(() => {
        this.refreshUser();
        this.checkLastPlayedEpisode();
      });
    },
    toggleNavBar(show) {
      this.showNavBar = show;
    },
    checkLastPlayedEpisode() {
      const eepisodeService = new EpisodeService();
      eepisodeService.playLastPlayedEpisode();
    },
    refreshUser() {
      this.userService.refreshUser().then((user) => {
        this.$analytics.identify(user.uuid);
      });
    },
    onTapDownload() {
      this.tappedDownloadOnMobileBanner = true;
    },
    getUserEpisodeListens() {
      this.episodeService.getListenedEpisodes().then((episodesProgress) => {
        this.$store.dispatch('setAllEpisodesProgress', episodesProgress || []);
      });
    },
    fetchSubscription() {
      this.subscriptionProvider.getSubscription().then((resp) => {
        if (resp && resp.uuid) {
          this.$store.commit('setUserSubscription', resp);
          const defaultPricing = {
            currency: resp.currency,
            price: resp.total_price,
          };
          this.$store.commit('setPricing', defaultPricing);
        }
      });
    },
    fetchSubscriptionPlans() {
      this.loader = true;
      const countryCode = this.$store.getters.country || 'US';
      let country = this.countries.find(nextItem => nextItem.code === countryCode);
      // if user belongs to country other than US/UK/AU/CA then show pricing for US
      if (!country) {
        country = this.countries.find(nextItem => nextItem.code === 'US');
      }
      this.defaultCurrency = country.currency;
      this.subscriptionProvider.fetchSubscriptionPlans(country.currency).then((plan) => {
        this.loader = false;
        this.$store.commit('setPlansLoaded', true);
        if (plan && plan.productRatePlanCharges && plan.productRatePlanCharges.length) {
          this.$store.commit('setActivePlan', plan);

          this.setDefaultPricing(plan);
          if (!this.supportedCountries.includes(countryCode)) {
            this.loader = false;
            return;
          }
          this.readBranchLinkData();
        }
      })
        .catch(() => {
          this.loader = false;
        });
    },
    setDefaultPricing(plan) {
      let countryCode = this.$store.getters.country || 'US';
      countryCode = countryCode.toLowerCase();
      let country = this.countries.find(nextItem => nextItem.code.toLowerCase() === countryCode);
      // if user belongs to country other than US/UK/AU/CA then show pricing for US
      if (!country) {
        country = this.countries.find(nextItem => nextItem.code === 'US');
      }

      const productRatePlanCharges = plan.productRatePlanCharges[plan.productRatePlanCharges.length - 1];
      let pricing = productRatePlanCharges.pricing.find(nextItem => nextItem.currency === country.currency);

      // if user have subscribed before and country in user's subscription record is different from default country
      // then set default pricing of country found in user's subscription record
      const { userSubscription } = this.$store.getters;
      if (userSubscription && userSubscription.uuid) {
        const totalPrice = this.validateTotalPriceLocally(userSubscription.country, userSubscription.total_price);
        pricing = {
          currency: userSubscription.currency,
          price: totalPrice,
        };
      }
      if (pricing && pricing.price && pricing.currency) {
        this.$store.commit('setPricing', pricing);
      }
    },
    readBranchLinkData() {
      // Read Deep link data and navigate user accordingly
      this.loader = true;
      this.branchService.readDeeplink()
        .then((data) => {
          this.loader = false;
          // this.storeBranchData(data.data);
          this.storeBranchLink(data.data_parsed);
          if (data.data_parsed && data.data_parsed.podcast_uuid && !data.data_parsed.$fallback_url) {
            this.$router.push(`/listen/${data.data_parsed.publisher_slug}/${data.data_parsed.show_slug}/${data.data_parsed.podcast_uuid}`);
          } else if (data.data_parsed && data.data_parsed.episode_uuid && !data.data_parsed.$fallback_url) {
            this.$router.push(`/listen/${data.data_parsed.publisher_slug}/${data.data_parsed.show_slug}/${data.data_parsed.episode_slug}/${data.data_parsed.episode_uuid}`);
          } else if (data.data_parsed && data.data_parsed.promo_code) {
            this.loader = true;
            this.promoCodeHandling(data.data_parsed.promo_code, data.data_parsed.promo_header, data.data_parsed.promo_subheader);
          } else if (data.data_parsed && data.data_parsed.referee) {
            this.creatorLinksHandling(data.data_parsed.referee, data.data_parsed.referee_first_name);
          }
        })
        .catch((error) => {
          log.warn(error.message);
          this.loader = false;
        });
    },
    setUserCountry() {
      const urlParams = new URLSearchParams(window.location.search);
      let countryCode = urlParams.get('country');

      let defaultCountry;
      if (this.$route && this.$route.query.country) {
        // Store it in the local variable
        defaultCountry = this.$route.query.country;
        // Remove country code from query param
        const query = Object.assign({}, this.$route.query);
        delete query.country;
        this.$router.replace({ query });
      } else if (countryCode) {
        defaultCountry = countryCode;
      } else {
        defaultCountry = (this.$store && this.$store.getters && this.$store.getters.country) || 'US';
      }
      // set default pricing before it load from products
      let country = this.countries.find(nextItem => nextItem.code === defaultCountry);
      // if user belongs to country other than US/UK/AU/CA then show pricing for US
      if (!country) {
        country = this.countries.find(nextItem => nextItem.code === 'US');
      }
      this.$store.commit('setPricing', { currency: country.currency, price: country.price });

      // Store it for future references
      this.$store.dispatch('setUserCountry', defaultCountry);
      if (!this.supportedCountries.includes(defaultCountry)) {
        this.$router.push('/international');
      }
    },
    adFrameUrlFix() {
      // Force scripts to be loaded over https
      document.getElementById('adframe').setAttribute('src', `https://${window.location.hostname}/static/adframe.js`);
      // const elem = document.getElementsByTagName('script');
      // if (!elem) return;
      // Array.from(elem).forEach((item) => {
      //   if (!item.src.includes('adframe')) return;
      //   item.src = `https://${window.location.hostname}/static/adframe.js`;
      // });
    },
    openPromoOfferModal() {
      this.showPromoOfferModal = true;
    },
    closePromoOfferModal(flag) {
      this.showPromoOfferModal = false;
      if (flag) {
        this.$router.push('/signup');
      }
    },
    promoLinkValidateResponse(resp) {
      this.loader = false;
      if (resp && resp.valid) {
        this.promoData = resp.promoData;
        this.openPromoOfferModal();
      }
    },
    validateTotalPriceLocally(country, totalPrice) {
      const { pricing } = this.$store.getters;
      const targetCountry = this.countries.find(countryCode => countryCode === country);
      if (totalPrice > 0) {
        return totalPrice;
      }
      return targetCountry && targetCountry.price ? targetCountry.price : pricing.price;
    },
    getBrowsersFingerPrintId() {
      if (!window.branch || (window.branch && !window.branch.getBrowserFingerprintId)) {
        return;
      }
      window.branch.getBrowserFingerprintId((err, data) => {
        if (data) {
          localStorage.setItem('branch_fingerprint_id', data);
        }
      });
    },
    promoCodeHandling(promoCode, promoHeader, promoSubheader) {
      const { country } = this.$store.getters;
      if (!this.supportedCountries.includes(country)) {
        this.loader = false;
        return;
      }
      const promoData = {
        promoCode: '',
        trialPeriod: 1,
        trialEndDate: '',
        header: promoHeader,
        subheader: promoSubheader,
      };
      this.$store.dispatch('setPromoData', promoData);
      this.subscriptionProvider.validateAndStorePromoCode(promoCode, this.defaultCurrency)
        .then(this.promoLinkValidateResponse)
        .catch(() => {
          this.loader = false;
        });
    },
    creatorLinksHandling(creatorUsername, creatorFirstname) {
      const flag = this.creators.includes(creatorUsername);
      if (!flag) {
        return;
      }
      const promoCode = 'creators';
      const promoHeader = 'Welcome to Luminary';
      const promoSubheader = "You've unlocked a special welcome offer! Sign up for Luminary Premium and access your first three months of premium content for free. After that, the service is $7.99 per month and you can cancel anytime. Terms apply.";
      this.promoCodeHandling(promoCode, promoHeader, promoSubheader);
    },
    storeBranchData(stringifiedData) {
      if (stringifiedData === '') {
        return;
      }
      const data = JSON.parse(stringifiedData);
      Object.getOwnPropertyNames(data).forEach((key) => {
        if (typeof data[key] === 'boolean' || typeof data[key] === 'number') {
          data[key] = data[key].toString();
        }
      });
      localStorage.setItem('branch_data', JSON.stringify(data));
    },
    triggerLoadAnalytics() {
      let consentManagerElement = document.getElementById("segment-consent-manager");
      if (consentManagerElement) {
        consentManagerElement.click();
        this.$analytics.page('');
      }
    },
    storeBranchLink(dataParsed) {
      if (dataParsed && dataParsed['~referring_link'])  {
        const referring_link = dataParsed['~referring_link'];
        const branch_promo = referring_link.split('/').pop();
        localStorage.setItem('branch_promo', branch_promo);
      }
    },
  },
};
</script>

<style lang="scss">
#app.audio-player-visible {
  margin-bottom: 70px;
}
#app.auth-footer-visible {
  margin-bottom: 60px;
}
#app.audio-player-visible.auth-footer-visible {
  margin-bottom: 132px;

  // .audio-player {
  //   bottom: 61px;
  // }
}
.fade-enter-active {
  transition: .4s all ease-in;
}

.fade-enter,
.fade-leave-active {
  opacity: 0;
}
.main-container {
  min-height:100vh;
  display: -webkit-flex;
  display: flex;
  flex-direction: column;

  > div:nth-child(2) {
    position: relative;
    flex: 1 0 auto;

    @media (max-width: 768px) {
      min-height: 600px;
    }
  }
}

#segment-consent-manager {
  position: fixed;
  bottom: 0;
  left: 50%;

  display: none;

  transform: translateX(-50%);

  width: 92%;

  border: 1px solid #BABABA;
  border-bottom: none;

  border-radius: 10px;

  > div > div {
    background-color: white;
  }
}

div[data-consent-manager-dialog] > div {
  z-index: 10001;
  pointer-events: none;
}

div[data-consent-manager-dialog] > div > section {
  z-index: 10001;
  pointer-events: auto;
  width: 92%;
  margin: 0;
  border-radius: 7px 7px 0 0;
  bottom: 0;
  position: absolute;
  max-width: none;

  font-family: Favorit;

  // animation: none;

  padding: 25px;
  box-sizing: border-box;

  > div {
    border-bottom: none;
    padding: 0;
    margin-bottom: 20px;

    h2 {
      font-size: 20px;
      color: #111111;
    }

    button {
      display: none;
    }
  }

  > form {
    > div {
      padding: 0;
      color: #666 !important;

      @media (max-width: 567px) {
        font-size: 12px !important;
      }

      &:nth-child(1) {
        > div {
          margin-top: 30px;
        }

        thead {
          th {
            background-color: #F8F8F8;
            color: #111;
            font-size: 16px;
          }
        }

        tbody {
          font-size: 14px;
        }

        @media (max-width: 567px) {
          table {
            th, td {
              font-size: 12px;

              > p {
                line-height: 18px;
              }
            }
          }
        }
      }

      &:nth-child(2) {
        margin-top: 30px;
        display: flex;

        > div {
          button {
            border-radius: 25px;
            font-size: 16px;

            padding: 12px 30px;
            height: auto;
            min-width: 102px;

            &:first-child {
              display: none;
            }
            // cursor: pointer;
          }

          button[type=submit] {
            background-color: #00AECB;
            background-image: none;
          }
        }
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// app/src/App.vue